import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  Fade,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { keyframes } from '@emotion/react';
import AddHomeWorkOutlinedIcon from '@mui/icons-material/AddHomeWorkOutlined';
import AddHomeWorkRoundedIcon from '@mui/icons-material/AddHomeWorkRounded';
import EditNoteOutlinedIcon from '@mui/icons-material/EditNoteOutlined';
import EditNoteRoundedIcon from '@mui/icons-material/EditNoteRounded';
import QueryStatsOutlinedIcon from '@mui/icons-material/QueryStatsOutlined';
import QueryStatsRoundedIcon from '@mui/icons-material/QueryStatsRounded';
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined';
import CampaignRoundedIcon from '@mui/icons-material/CampaignRounded';
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import PriorityHighRoundedIcon from '@mui/icons-material/PriorityHighRounded';
import CelebrationRoundedIcon from '@mui/icons-material/CelebrationRounded';
import VerifiedRoundedIcon from '@mui/icons-material/VerifiedRounded';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import { PROPERTY_CREATION_STEP_STATUS } from '../constants/propertyCreationWorkflow';

const { PENDING, IN_PROGRESS, COMPLETED, FAILED } = PROPERTY_CREATION_STEP_STATUS;

const doneLikeStatuses = new Set([COMPLETED, FAILED]);

// Outlined while waiting, filled once the step has produced something.
const STEP_ICONS = {
  create_property: { outlined: AddHomeWorkOutlinedIcon, filled: AddHomeWorkRoundedIcon },
  generate_public_remarks: { outlined: EditNoteOutlinedIcon, filled: EditNoteRoundedIcon },
  generate_cma_report: { outlined: QueryStatsOutlinedIcon, filled: QueryStatsRoundedIcon },
  generate_marketing_strategy: { outlined: CampaignOutlinedIcon, filled: CampaignRoundedIcon },
};
const DEFAULT_STEP_ICON = { outlined: AutoAwesomeRoundedIcon, filled: AutoAwesomeRoundedIcon };

const TILE_SIZE = 56;
const TILE_RADIUS = 16;
const RING_INSET = 5;
// Perimeter of the rounded square the running arc travels along.
const RING_SIZE = TILE_SIZE + RING_INSET * 2;
const RING_RADIUS = TILE_RADIUS + RING_INSET - 1;
const RING_STROKE = 2.5;
const RING_SIDE = RING_SIZE - RING_STROKE;
const RING_PERIMETER = Math.round(4 * (RING_SIDE - 2 * RING_RADIUS) + 2 * Math.PI * RING_RADIUS);
const RING_DASH = 60;

const LIVE_MESSAGE_INTERVAL_MS = 1500;
const LIVE_MESSAGE_FADE_MS = 250;

// Share of the header percentage a step is worth (see the workflow constants).
const getStepWeight = (step) => {
  const weight = Number(step?.weight);
  return Number.isFinite(weight) && weight > 0 ? weight : 1;
};

const runArc = keyframes`
  to { stroke-dashoffset: -${RING_PERIMETER}px; }
`;
const floatIcon = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-3px); }
`;
// A light band sweeping along the working segment: enters from the left,
// fully exits on the right, then repeats.
const sheen = keyframes`
  0% { transform: translateX(-100%); }
  100% { transform: translateX(250%); }
`;
const pulseDot = keyframes`
  0% { box-shadow: 0 0 0 0 var(--pulse-color); }
  100% { box-shadow: 0 0 0 10px transparent; }
`;
const bounceDot = keyframes`
  0%, 60%, 100% { transform: translateY(0); opacity: 0.6; }
  30% { transform: translateY(-3px); opacity: 1; }
`;

const formatPrice = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num) || num <= 0) {
    return null;
  }
  return num.toLocaleString('en-CA', {
    style: 'currency',
    currency: 'CAD',
    maximumFractionDigits: 0,
  });
};

// Build a human-friendly detail line for a step once results are available.
const getResultDetail = (stepId, result) => {
  if (!result) {
    return '';
  }

  const seededProperty = result.seeded_property || {};

  switch (stepId) {
    case 'create_property': {
      const photos = Number(result.imported_photos || 0);
      return photos > 0
        ? `${photos} photo${photos === 1 ? '' : 's'} imported from the previous listing.`
        : 'Listing created. No photos were imported — you can upload them on the property page.';
    }
    case 'generate_public_remarks':
      return result.public_remark_generated
        ? 'Listing description generated from the existing details.'
        : 'Could not generate a listing description. Please add it manually.';
    case 'generate_cma_report': {
      const cma = result.cma_generated || {};
      if (cma.success) {
        const price = formatPrice(seededProperty.ListPrice);
        return price
          ? `CMA report ready. Suggested list price set to ${price}.`
          : 'CMA report ready and a suggested price was applied.';
      }
      return cma.message || 'Could not generate a CMA report or set a suggested price.';
    }
    case 'generate_marketing_strategy': {
      const marketing = result.marketing_strategy_generated || {};
      return marketing.success
        ? 'Marketing strategy created for this property.'
        : marketing.message || 'Could not generate a marketing strategy.';
    }
    default:
      return '';
  }
};

// Resolve every colour the modal uses from the platform theme so the
// component follows palette changes without edits here.
const useModalTokens = () => {
  const theme = useTheme();
  return useMemo(() => {
    const { slate, blue, mainColor: jade } = theme.palette.modern;
    const primary = theme.palette.primary.main;
    const success = theme.palette.success.main;
    const warning = theme.palette.warning.main;
    return {
      slate,
      blue,
      jade,
      primary,
      success,
      warning,
      warningText: theme.palette.warning.dark,
      warnSoft: alpha(theme.palette.warning.light, 0.22),
      warnLine: alpha(warning, 0.45),
      primaryHalo: alpha(primary, 0.16),
      highlight: alpha(theme.palette.common.white, 0.75),
      paper: theme.palette.background.paper,
      paperGradient: theme.modern?.colors?.gradient?.white || theme.palette.background.paper,
      headingFont: theme.typography.h6.fontFamily,
      motion: theme.modern?.motion?.emphasis || '320ms cubic-bezier(0.2, 0.8, 0.2, 1)',
      motionStandard: theme.modern?.motion?.standard || '240ms ease',
      liftShadow: theme.shadows[4],
    };
  }, [theme]);
};

// Rotates through a step's live messages while it is in progress.
const useLiveMessage = (activeStep, enabled) => {
  const messages = useMemo(() => {
    if (!activeStep) {
      return [];
    }
    const list = Array.isArray(activeStep.liveMessages) && activeStep.liveMessages.length
      ? activeStep.liveMessages
      : [activeStep.description || activeStep.label];
    return list.filter(Boolean);
  }, [activeStep]);

  const [index, setIndex] = useState(0);
  const [visible, setVisible] = useState(true);
  const activeId = activeStep?.id;

  useEffect(() => {
    setIndex(0);
    setVisible(true);
    if (!enabled || messages.length < 2) {
      return undefined;
    }
    let swapTimeout = null;
    const interval = setInterval(() => {
      setVisible(false);
      swapTimeout = setTimeout(() => {
        setIndex((prev) => (prev + 1) % messages.length);
        setVisible(true);
      }, LIVE_MESSAGE_FADE_MS);
    }, LIVE_MESSAGE_INTERVAL_MS);
    return () => {
      clearInterval(interval);
      if (swapTimeout) {
        clearTimeout(swapTimeout);
      }
    };
  }, [activeId, enabled, messages]);

  return { text: messages[index] || messages[0] || '', visible };
};

const WorkingDots = () => (
  <Box component="span" sx={{ display: 'inline-flex', gap: '2px', ml: 0.5 }}>
    {[0, 1, 2].map((i) => (
      <Box
        key={i}
        component="i"
        sx={{
          width: 3,
          height: 3,
          borderRadius: '50%',
          backgroundColor: 'currentColor',
          animation: `${bounceDot} 1s ease-in-out infinite`,
          animationDelay: `${i * 0.15}s`,
          '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
        }}
      />
    ))}
  </Box>
);

const StepTile = ({ stepId, status, animate, tokens }) => {
  const icons = STEP_ICONS[stepId] || DEFAULT_STEP_ICON;
  const Icon = status === COMPLETED ? icons.filled : icons.outlined;
  const isActive = status === IN_PROGRESS;
  const isDone = status === COMPLETED;
  const isFailed = status === FAILED;

  const tileStyles = {
    [PENDING]: { backgroundColor: tokens.slate[100], color: tokens.slate[400] },
    [IN_PROGRESS]: { backgroundColor: tokens.blue[100], color: tokens.primary },
    [COMPLETED]: { backgroundColor: tokens.jade[100], color: tokens.success },
    [FAILED]: { backgroundColor: tokens.warnSoft, color: tokens.warning },
  };

  return (
    <Box
      sx={{
        position: 'relative',
        width: TILE_SIZE,
        height: TILE_SIZE,
        borderRadius: `${TILE_RADIUS}px`,
        display: 'grid',
        placeItems: 'center',
        transition: `background-color ${tokens.motion}, color ${tokens.motion}`,
        zIndex: 2,
        flex: 'none',
        ...(tileStyles[status] || tileStyles[PENDING]),
      }}
    >
      {isActive && (
        <Box
          component="svg"
          aria-hidden
          viewBox={`0 0 ${RING_SIZE} ${RING_SIZE}`}
          sx={{
            position: 'absolute',
            inset: -RING_INSET,
            width: RING_SIZE,
            height: RING_SIZE,
            color: tokens.primary,
            overflow: 'visible',
            // Keyframes must go through emotion (sx), not an inline style.
            // The arc is the step's progress indicator, so it runs even when
            // the OS asks for reduced motion; decorations respect that flag.
            '& rect': {
              animation: `${runArc} 1.4s linear infinite`,
            },
          }}
        >
          <rect
            x={RING_STROKE / 2}
            y={RING_STROKE / 2}
            width={RING_SIDE}
            height={RING_SIDE}
            rx={RING_RADIUS}
            fill="none"
            stroke="currentColor"
            strokeWidth={RING_STROKE}
            strokeLinecap="round"
            strokeDasharray={`${RING_DASH} ${RING_PERIMETER - RING_DASH}`}
          />
        </Box>
      )}

      <Icon
        sx={{
          fontSize: 30,
          animation: isActive && animate ? `${floatIcon} 1.8s ease-in-out infinite` : 'none',
        }}
      />

      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          right: -7,
          bottom: -7,
          width: 22,
          height: 22,
          borderRadius: '50%',
          display: 'grid',
          placeItems: 'center',
          color: '#ffffff',
          backgroundColor: isFailed ? tokens.warning : tokens.success,
          border: `2.5px solid ${tokens.paper}`,
          transform: isDone || isFailed ? 'scale(1)' : 'scale(0)',
          transition: animate
            ? 'transform 350ms cubic-bezier(0.34, 1.56, 0.64, 1)'
            : 'none',
        }}
      >
        {isFailed
          ? <PriorityHighRoundedIcon sx={{ fontSize: 14 }} />
          : <CheckRoundedIcon sx={{ fontSize: 14 }} />}
      </Box>
    </Box>
  );
};

const StepCard = ({ step, status, index, total, isResults, detail, animate, tokens }) => {
  const isActive = status === IN_PROGRESS && !isResults;
  const isDone = status === COMPLETED;
  const isFailed = status === FAILED;
  const isPending = status === PENDING;
  const connectorDone = doneLikeStatuses.has(status);
  const label = step.shortLabel || step.label;

  const stateStyles = isActive
    ? {
      border: `1.5px solid ${tokens.primary}`,
      background: `linear-gradient(180deg, ${tokens.blue[100]} 0%, ${tokens.paper} 55%)`,
      boxShadow: `${tokens.liftShadow}, 0 0 0 4px ${tokens.primaryHalo}`,
      transform: animate ? 'translateY(-3px)' : 'none',
    }
    : isDone
      ? { border: `1.5px solid ${tokens.jade[300]}`, background: tokens.paperGradient }
      : isFailed
        ? {
          border: `1.5px solid ${tokens.warnLine}`,
          background: `linear-gradient(180deg, ${tokens.warnSoft} 0%, ${tokens.paper} 60%)`,
        }
        : { border: `1.5px dashed ${tokens.slate[300]}`, background: tokens.paper };

  const connectorColor = isFailed ? tokens.warning : tokens.success;

  return (
    <Box
      role="listitem"
      aria-label={`Step ${index + 1} of ${total}, ${label}, ${status.replace('_', ' ')}`}
      sx={{
        position: 'relative',
        display: 'grid',
        gridTemplateColumns: { xs: `${TILE_SIZE}px 1fr`, sm: '1fr' },
        alignItems: { xs: 'center', sm: 'stretch' },
        justifyItems: { xs: 'stretch', sm: 'center' },
        gap: { xs: 1.75, sm: 1.5 },
        p: { xs: '12px 14px', sm: '16px 14px 14px' },
        borderRadius: '14px',
        minWidth: 0,
        transition: `border-color ${tokens.motion}, background ${tokens.motion}, box-shadow ${tokens.motion}, transform ${tokens.motion}`,
        ...stateStyles,
        // Connector to the next card: horizontal on wide screens, vertical when stacked.
        '&:not(:last-of-type)::after': {
          content: '""',
          position: 'absolute',
          zIndex: 1,
          backgroundColor: tokens.slate[200],
          backgroundImage: `linear-gradient(${connectorColor}, ${connectorColor})`,
          backgroundRepeat: 'no-repeat',
          transition: animate ? 'background-size 600ms ease' : 'none',
          top: { xs: 12 + TILE_SIZE, sm: 16 + TILE_SIZE / 2 - 1 },
          left: { xs: 14 + TILE_SIZE / 2 - 1, sm: `calc(50% + ${TILE_SIZE / 2}px)` },
          width: { xs: 2, sm: `calc(100% - ${TILE_SIZE - 14}px)` },
          height: { xs: 'calc(100% - 44px)', sm: 2 },
          backgroundSize: {
            xs: connectorDone ? '100% 100%' : '100% 0%',
            sm: connectorDone ? '100% 100%' : '0% 100%',
          },
        },
      }}
    >
      <StepTile stepId={step.id} status={status} animate={animate} tokens={tokens} />

      <Box
        sx={{
          display: 'grid',
          gap: 0.5,
          minWidth: 0,
          textAlign: { xs: 'left', sm: 'center' },
          justifyItems: { xs: 'start', sm: 'center' },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'row', sm: 'column' },
            alignItems: 'center',
            gap: 0.75,
            flexWrap: 'wrap',
            justifyContent: { xs: 'flex-start', sm: 'center' },
          }}
        >
          <Typography
            component="span"
            sx={{
              fontFamily: tokens.headingFont,
              fontSize: '0.85rem',
              fontWeight: 600,
              lineHeight: 1.3,
              color: isPending ? tokens.slate[400] : tokens.slate[900],
              transition: `color ${tokens.motionStandard}`,
            }}
          >
            {label}
          </Typography>

          {isActive && (
            <Chip
              size="small"
              label={(
                <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center' }}>
                  Working
                  {animate && <WorkingDots />}
                </Box>
              )}
              sx={{
                height: 20,
                borderRadius: '999px',
                backgroundColor: tokens.blue[100],
                color: tokens.primary,
                '& .MuiChip-label': { px: 1, fontSize: '0.68rem', fontWeight: 700 },
              }}
            />
          )}
          {isFailed && (
            <Chip
              size="small"
              label="Needs attention"
              sx={{
                height: 20,
                borderRadius: '999px',
                backgroundColor: tokens.warnSoft,
                color: tokens.warningText,
                border: `1px solid ${tokens.warnLine}`,
                '& .MuiChip-label': { px: 1, fontSize: '0.68rem', fontWeight: 700 },
              }}
            />
          )}
        </Box>

        {detail ? (
          <Typography
            component="p"
            sx={{
              m: 0,
              fontSize: '0.78rem',
              lineHeight: 1.45,
              color: isPending
                ? tokens.slate[400]
                : isFailed
                  ? tokens.warningText
                  : tokens.slate[600],
              transition: `color ${tokens.motionStandard}`,
            }}
          >
            {detail}
          </Typography>
        ) : null}
      </Box>
    </Box>
  );
};

const SegmentedProgress = ({ steps, stepStatuses, isResults, animate, tokens }) => (
  <Box
    aria-hidden
    sx={{
      display: 'grid',
      gridTemplateColumns: `repeat(${Math.max(steps.length, 1)}, 1fr)`,
      gap: '6px',
      px: { xs: 2, sm: 3.25 },
      pb: { xs: 1.5, sm: 2.25 },
    }}
  >
    {steps.map((step) => {
      const status = stepStatuses[step.id] || PENDING;
      const active = status === IN_PROGRESS && !isResults;
      const fillColor = status === FAILED ? tokens.warning : tokens.success;
      return (
        <Box
          key={step.id}
          sx={{
            position: 'relative',
            height: 6,
            borderRadius: '999px',
            backgroundColor: tokens.slate[200],
            overflow: 'hidden',
          }}
        >
          {/* Colour carries the state: teal while the step is working, green
              once it is done, amber if it needs attention. */}
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              borderRadius: '999px',
              overflow: 'hidden',
              width: active || doneLikeStatuses.has(status) ? '100%' : '0%',
              backgroundColor: active ? tokens.primary : fillColor,
              transition: animate
                ? `width 600ms ease, background-color ${tokens.motionStandard}`
                : 'none',
            }}
          >
            {/* A light sheen sweeps along the working segment. Like the arc,
                it is the progress indicator itself, so it is not gated by
                the reduced-motion flag. */}
            {active && (
              <Box
                aria-hidden
                sx={{
                  position: 'absolute',
                  top: 0,
                  bottom: 0,
                  left: 0,
                  width: '45%',
                  background: `linear-gradient(90deg, transparent, ${tokens.highlight}, transparent)`,
                  animation: `${sheen} 1.2s ease-in-out infinite`,
                }}
              />
            )}
          </Box>
        </Box>
      );
    })}
  </Box>
);

const PropertyCreationProgressModal = ({
  open,
  title = 'Preparing your listing',
  subtitle = '',
  steps = [],
  stepStatuses = {},
  result = null,
  onConfirm,
  confirmLabel = 'Go to property dashboard',
}) => {
  const tokens = useModalTokens();
  // `animate` gates decorative motion (card lift, floating icon, dots, pulse,
  // fades). The two progress indicators, the arc around the active tile and
  // the sheen on its bar segment, always run so the wait never looks stalled.
  const reduceMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
  const animate = !reduceMotion;

  const isResults = Boolean(result);
  const totalSteps = Math.max(steps.length, 1);

  const readyCount = steps.filter((step) => stepStatuses[step.id] === COMPLETED).length;
  const activeIndex = steps.findIndex((step) => stepStatuses[step.id] === IN_PROGRESS);
  const activeStep = activeIndex >= 0 ? steps[activeIndex] : null;

  // Percentage jumps at step boundaries, weighted by each step's `weight` so
  // the slow LLM steps count for more: finished steps count in full, the
  // active step counts half. Defaults give 6 → 22 → 50 → 83 → 100.
  const totalWeight = steps.reduce((sum, step) => sum + getStepWeight(step), 0) || 1;
  const doneWeight = steps.reduce(
    (sum, step) => sum + (doneLikeStatuses.has(stepStatuses[step.id]) ? getStepWeight(step) : 0),
    0
  );
  const activeWeight = activeStep ? getStepWeight(activeStep) : 0;
  const progressValue = isResults
    ? 100
    : Math.round(((doneWeight + 0.5 * activeWeight) / totalWeight) * 100);

  const hasFailure = isResults && steps.some((step) => stepStatuses[step.id] === FAILED);

  const live = useLiveMessage(activeStep, open && !isResults && animate);

  const eyebrow = isResults
    ? 'Listing setup · Complete'
    : `Listing setup · Step ${Math.max(activeIndex, 0) + 1} of ${totalSteps}`;

  const headerTitle = isResults ? 'Property created successfully' : title;
  const headerSubtitle = isResults
    ? (hasFailure
      ? `${readyCount} of ${totalSteps} steps ready. The rest can be regenerated from the property dashboard.`
      : 'Your listing and its core materials are ready.')
    : subtitle;

  const headerTint = isResults
    ? (hasFailure ? tokens.warnSoft : tokens.jade[100])
    : tokens.blue[100];
  const accentColor = isResults
    ? (hasFailure ? tokens.warningText : tokens.success)
    : tokens.primary;

  return (
    <Dialog
      open={open}
      maxWidth={false}
      disableEscapeKeyDown
      onClose={undefined}
      aria-labelledby="property-creation-progress-title"
      PaperProps={{
        sx: {
          width: '100%',
          maxWidth: { xs: '100%', sm: 880 },
          overflow: 'hidden',
        },
      }}
    >
      {/* Header: eyebrow, title, subtitle and the percentage */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: 2.5,
          px: { xs: 2, sm: 3.25 },
          pt: { xs: 2.25, sm: 2.75 },
          pb: 2,
          background: `linear-gradient(135deg, ${headerTint} 0%, ${tokens.paper} 70%)`,
          transition: `background ${tokens.motion}`,
        }}
      >
        <Box sx={{ display: 'grid', gap: 0.5, minWidth: 0 }}>
          <Typography
            component="span"
            sx={{
              fontFamily: tokens.headingFont,
              fontSize: '0.68rem',
              fontWeight: 600,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              color: tokens.slate[500],
            }}
          >
            {eyebrow}
          </Typography>
          <Typography
            id="property-creation-progress-title"
            variant="h6"
            component="h2"
            sx={{ fontWeight: 700, lineHeight: 1.25, color: tokens.slate[900] }}
          >
            {headerTitle}
          </Typography>
          {headerSubtitle ? (
            <Typography variant="body2" sx={{ color: tokens.slate[600] }}>
              {headerSubtitle}
            </Typography>
          ) : null}
        </Box>

        <Box
          aria-hidden
          sx={{
            display: 'inline-flex',
            alignItems: 'baseline',
            gap: '2px',
            pt: 0.5,
            fontFamily: tokens.headingFont,
            fontWeight: 800,
            fontSize: '1.9rem',
            lineHeight: 1,
            color: accentColor,
            fontVariantNumeric: 'tabular-nums',
            whiteSpace: 'nowrap',
            transition: `color ${tokens.motion}`,
          }}
        >
          {isResults && !hasFailure ? (
            <CelebrationRoundedIcon sx={{ fontSize: 32 }} />
          ) : (
            <>
              {progressValue}
              <Box component="small" sx={{ fontSize: '0.9rem', fontWeight: 700, color: tokens.slate[500] }}>
                %
              </Box>
            </>
          )}
        </Box>
      </Box>

      <SegmentedProgress
        steps={steps}
        stepStatuses={stepStatuses}
        isResults={isResults}
        animate={animate}
        tokens={tokens}
      />

      <DialogContent sx={{ px: { xs: 2, sm: 3.25 }, pt: 0.75, pb: 1, '&.MuiDialogContent-root': { px: { xs: 2, sm: 3.25 } } }}>
        <Box
          role="list"
          aria-label="Listing setup steps"
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: `repeat(${totalSteps}, 1fr)` },
            gap: { xs: 1.5, sm: 1.75 },
          }}
        >
          {steps.map((step, index) => {
            const status = stepStatuses[step.id] || PENDING;
            let detail = step.description;
            if (isResults) {
              detail = getResultDetail(step.id, result) || step.description;
            } else if (status === COMPLETED) {
              detail = 'Done';
            }
            return (
              <StepCard
                key={step.id || index}
                step={step}
                status={status}
                index={index}
                total={totalSteps}
                isResults={isResults}
                detail={detail}
                animate={animate}
                tokens={tokens}
              />
            );
          })}
        </Box>

        {/* Footer strip: live status while working, summary once finished */}
        <Box sx={{ mt: 2, display: 'grid', gap: 1.5 }}>
          {!isResults && (
            <Box
              aria-live="polite"
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.25,
                minHeight: 24,
                fontSize: '0.85rem',
                color: tokens.slate[600],
              }}
            >
              <Box
                aria-hidden
                sx={{
                  '--pulse-color': tokens.primaryHalo,
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  backgroundColor: tokens.primary,
                  flex: 'none',
                  animation: animate ? `${pulseDot} 1.6s ease-out infinite` : 'none',
                }}
              />
              <Box
                component="span"
                sx={{
                  opacity: live.visible ? 1 : 0,
                  transition: animate ? `opacity ${LIVE_MESSAGE_FADE_MS}ms ease` : 'none',
                }}
              >
                {live.text}
              </Box>
            </Box>
          )}

          {isResults && !hasFailure && (
            <Fade in>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.25,
                  fontSize: '0.85rem',
                  color: tokens.slate[600],
                }}
              >
                <VerifiedRoundedIcon sx={{ color: tokens.success, fontSize: 20, flex: 'none' }} />
                <span>
                  Everything is ready. Your listing, remarks, CMA report and marketing
                  strategy are on the property dashboard.
                </span>
              </Box>
            </Fade>
          )}

          {isResults && hasFailure && (
            <Fade in>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 1.25,
                  p: '12px 14px',
                  borderRadius: '12px',
                  border: `1px solid ${tokens.warnLine}`,
                  backgroundColor: tokens.warnSoft,
                  fontSize: '0.85rem',
                  color: tokens.slate[800],
                }}
              >
                <ErrorOutlineRoundedIcon sx={{ color: tokens.warning, fontSize: 20, flex: 'none' }} />
                <span>
                  Some steps need your attention. You can regenerate the CMA report and marketing
                  strategy anytime from the property dashboard.
                </span>
              </Box>
            </Fade>
          )}
        </Box>
      </DialogContent>

      {isResults && (
        <DialogActions sx={{ px: { xs: 2, sm: 3.25 }, pb: { xs: 2, sm: 2.5 }, pt: 0.5 }}>
          <Button
            onClick={onConfirm}
            variant="contained"
            endIcon={<ArrowForwardRoundedIcon />}
            sx={{ fontWeight: 700 }}
          >
            {confirmLabel}
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default PropertyCreationProgressModal;
